import { useQuery } from '@tanstack/react-query';

import type { RatingCallRequest } from '@ctct/domain-layer';
import { planRepo } from '@ctct/domain-layer';

import { QueryKeys } from './constants';
import { useQueryMemoized } from '../helpers/useQueryMemoized';

type RatingQueryPayload = ReturnType<RatingCallRequest['toJson']>;

const getRatingPayloadFromQueryKey = (queryKey: readonly unknown[]): RatingQueryPayload | undefined => {
  if (queryKey[0] !== QueryKeys.RATING) {
    return undefined;
  }

  const payload = queryKey[1];

  if (
    payload === null ||
    typeof payload !== 'object' ||
    !('newPlanId' in payload) ||
    typeof (payload as { newPlanId: unknown }).newPlanId !== 'number'
  ) {
    return undefined;
  }

  return payload as RatingQueryPayload;
};

/**
 * Fresh rating on every payment-period change, including toggle-back.
 * staleTime/gcTime 0 match rise-ui `billing.rating.get`; refetchOnMount false avoids
 * redundant rating calls when components remount with the same query key.
 */
export const useRating = (ratingCallRequest: RatingCallRequest | null) => {
  const query = useQuery({
    queryKey: [QueryKeys.RATING, ratingCallRequest?.toJson()],
    queryFn: () => {
      if (!ratingCallRequest) {
        throw new Error('RatingPayload is required');
      }

      return planRepo.getPlanRating(ratingCallRequest.toJson());
    },
    placeholderData: (previousData, previousQuery) => {
      if (!previousData || !previousQuery || !ratingCallRequest) {
        return undefined;
      }

      const previousPayload = getRatingPayloadFromQueryKey(previousQuery.queryKey);
      const currentPayload = ratingCallRequest.toJson();

      if (!previousPayload || previousPayload.newPlanId !== currentPayload.newPlanId) {
        return undefined;
      }

      return previousData;
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: Boolean(ratingCallRequest),
  });

  return useQueryMemoized(query);
};

export type UseRating = ReturnType<typeof useRating>;
